import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { TInventory } from "~/types/inventory";
import { InventoryStatusCode } from "~/utils/constants";
import { capitalize, formatDimensions } from "~/utils";

export type ExportType = "csv" | "pdf" | "photo-list";

interface ExportData {
  [key: string]: string | number | undefined;
}

// Helper function to get client name
const getClientName = (client: TInventory["client"]) => {
  if (client.companyName) {
    return client.companyName;
  }
  const fullName = `${client.firstName || ""} ${client.lastName || ""}`.trim();
  return fullName || "Unknown";
};

// Helper function to render title with sub-client name if applicable
const renderTitle = (item: TInventory) => {
  const baseTitle = item.title || "";
  if (item.client?.isSubClient) {
    const subClientName = getClientName(item.client);
    return `${baseTitle}${baseTitle ? " " : ""}--${subClientName}--`;
  }
  return baseTitle;
};

// Format status text
const formatStatusText = (status?: InventoryStatusCode) => {
  const statusText = capitalize(status || "");
  if (statusText === "No Possession") {
    return "No AHC Possession";
  }
  return statusText;
};

// Get cell value for a column
const getCellValue = (item: TInventory, columnKey: string): string => {
  switch (columnKey) {
    case "inventoryRef":
      return item.inventoryRef || "-";
    case "clientInternalRef":
      return item.clientInternalRef || "-";
    case "title":
      return renderTitle(item);
    case "artistName":
      return item.artistName || "-";
    case "medium":
      return item.medium || "-";
    case "dimensions":
      return formatDimensions(item);
    case "packingType":
      return item.packingType
        ? capitalize(item.packingType.replace(/_/g, " "))
        : "-";
    case "status":
      return formatStatusText(item.status);
    case "location":
      return item.status === InventoryStatusCode.AVAILABLE_ON_HAND
        ? item.location?.name || "In Storage"
        : "-";
    case "value":
      return item.value ? `$${item.value.toLocaleString()}` : "-";
    case "weight":
      return item.weight ? `${item.weight} lbs` : "-";
    case "description":
      return item.description || "-";
    case "notes":
      return item.notes || "-";
    case "pieceCount":
      return item.pieceCount || "-";
    case "packageCount":
      return item.packageCount || "-";
    default:
      return "-";
  }
};

// Get column header label
const getColumnLabel = (columnKey: string): string => {
  const labels: { [key: string]: string } = {
    inventoryRef: "Ref #",
    clientInternalRef: "Client Ref #",
    title: "Title",
    artistName: "Artist",
    medium: "Medium",
    dimensions: "Dimensions",
    packingType: "Packing Type",
    status: "Status",
    location: "Location",
    value: "Value",
    weight: "Weight",
    description: "Description",
    notes: "Notes",
    pieceCount: "Piece Count",
    packageCount: "Package Count",
  };
  return labels[columnKey] || columnKey;
};

// Escape HTML special characters
const escapeHtml = (text: string): string => {
  const map: { [key: string]: string } = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };
  return text.replace(/[&<>"']/g, (m) => map[m]);
};

// Export to CSV
export const exportToCSV = (
  inventories: TInventory[],
  columns: string[],
  action: "view" | "download"
) => {
  // Create header row
  const headers = columns.map((col) => getColumnLabel(col));
  const headerRow = headers.join(",");

  // Create data rows
  const dataRows = inventories.map((item) => {
    return columns
      .map((col) => {
        const value = getCellValue(item, col);
        // Escape commas and quotes in CSV
        if (value.includes(",") || value.includes('"') || value.includes("\n")) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      })
      .join(",");
  });

  // Combine header and data
  const csvContent = [headerRow, ...dataRows].join("\n");

  // Create blob
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  if (action === "view") {
    // Open in new tab
    const newWindow = window.open(url, "_blank");
    if (!newWindow) {
      // Fallback to download if popup blocked
      const link = document.createElement("a");
      link.href = url;
      link.download = `inventory-export-${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } else {
    // Download
    const link = document.createElement("a");
    link.href = url;
    link.download = `inventory-export-${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Clean up URL after a delay
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

// Export to PDF
export const exportToPDF = (
  inventories: TInventory[],
  columns: string[],
  action: "view" | "download"
) => {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  // Set title
  doc.setFontSize(16);
  doc.text("Inventory Export", 14, 15);

  // Prepare table data
  const headers = columns.map((col) => getColumnLabel(col));
  const rows = inventories.map((item) =>
    columns.map((col) => getCellValue(item, col))
  );

  // Add table
  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: 20,
    styles: {
      fontSize: 8,
      cellPadding: 2,
    },
    headStyles: {
      fillColor: [0, 0, 0],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    margin: { top: 20 },
  });

  // Generate PDF blob
  const pdfBlob = doc.output("blob");
  const url = URL.createObjectURL(pdfBlob);

  if (action === "view") {
    // Open in new tab
    const newWindow = window.open(url, "_blank");
    if (!newWindow) {
      // Fallback to download if popup blocked
      const link = document.createElement("a");
      link.href = url;
      link.download = `inventory-export-${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } else {
    // Download
    doc.save(`inventory-export-${Date.now()}.pdf`);
  }

  // Clean up URL after a delay
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

// Helper: load image via <img> tag and draw to canvas to get base64 (avoids CORS fetch issues)
const fetchImageAsBase64 = (url: string): Promise<string | null> => {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) { resolve(null); return; }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    // Timeout after 5s per image
    setTimeout(() => resolve(null), 5000);
    img.src = url;
  });
};

// Export Photo List as PDF with embedded images
export const exportPhotoList = async (
  inventories: TInventory[],
  columns: string[],
  action: "view" | "download"
) => {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  const imgSize = 55;
  const detailsX = margin + imgSize + 10;
  const detailsWidth = contentWidth - imgSize - 10;

  // Title page header
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.text("Inventory Photo List", margin, 22);
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100);
  doc.text(`${inventories.length} item${inventories.length !== 1 ? "s" : ""}`, margin, 30);
  doc.setTextColor(0);
  doc.setDrawColor(200);
  doc.line(margin, 33, pageWidth - margin, 33);

  let cursorY = 40;

  for (let i = 0; i < inventories.length; i++) {
    const item = inventories[i];

    // Gather details
    const details = columns
      .filter((col) => col !== "title" && col !== "inventoryRef")
      .map((col) => ({ label: getColumnLabel(col), value: getCellValue(item, col) }))
      .filter((d) => d.value !== "-");

    // Estimate card height: title area + details rows (stacked single column, ~7mm each)
    const detailRows = details.length;
    const cardHeight = Math.max(imgSize + 5, 18 + detailRows * 7 + 5);

    // Page break if needed
    if (cursorY + cardHeight > pageHeight - margin) {
      doc.addPage();
      cursorY = margin;
    }

    // Card background
    doc.setFillColor(250, 250, 250);
    doc.setDrawColor(220);
    doc.roundedRect(margin, cursorY, contentWidth, cardHeight, 3, 3, "FD");

    // Image
    const imageUrl = item.images && item.images.length > 0 ? item.images[0].url : null;
    if (imageUrl) {
      const base64 = await fetchImageAsBase64(imageUrl);
      if (base64) {
        try {
          doc.addImage(base64, "JPEG", margin + 3, cursorY + 3, imgSize - 6, imgSize - 6);
        } catch {
          // Image failed to embed — draw placeholder
          doc.setFillColor(240, 240, 240);
          doc.rect(margin + 3, cursorY + 3, imgSize - 6, imgSize - 6, "F");
          doc.setFontSize(8);
          doc.setTextColor(160);
          doc.text("No Image", margin + imgSize / 2, cursorY + imgSize / 2, { align: "center" });
          doc.setTextColor(0);
        }
      }
    } else {
      doc.setFillColor(240, 240, 240);
      doc.rect(margin + 3, cursorY + 3, imgSize - 6, imgSize - 6, "F");
      doc.setFontSize(8);
      doc.setTextColor(160);
      doc.text("No Image", margin + imgSize / 2, cursorY + imgSize / 2, { align: "center" });
      doc.setTextColor(0);
    }

    // Title (big, bold)
    let textY = cursorY + 8;
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(26, 26, 46);
    const title = renderTitle(item) || "Untitled";
    doc.text(title, detailsX, textY, { maxWidth: detailsWidth });
    textY += 6;

    // Ref # (smaller, gray)
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(120);
    doc.text(`Ref: ${item.inventoryRef || "N/A"}`, detailsX, textY);
    textY += 6;

    // Divider line
    doc.setDrawColor(200);
    doc.line(detailsX, textY - 2, detailsX + detailsWidth, textY - 2);

    // Details — stacked single column, bigger text
    doc.setFontSize(10);
    for (const detail of details) {
      if (textY > cursorY + cardHeight - 4) break;
      doc.setFont("helvetica", "bold");
      doc.setTextColor(100);
      doc.text(`${detail.label}:`, detailsX, textY);
      const labelWidth = doc.getTextWidth(`${detail.label}: `);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(30);
      doc.text(detail.value, detailsX + labelWidth, textY, { maxWidth: detailsWidth - labelWidth });
      textY += 7;
    }

    cursorY += cardHeight + 6;
  }

  // Output
  const pdfBlob = doc.output("blob");
  const url = URL.createObjectURL(pdfBlob);

  if (action === "view") {
    const newWindow = window.open(url, "_blank");
    if (!newWindow) {
      doc.save(`inventory-photo-list-${Date.now()}.pdf`);
    }
  } else {
    doc.save(`inventory-photo-list-${Date.now()}.pdf`);
  }

  setTimeout(() => URL.revokeObjectURL(url), 100);
};

